import { useEffect } from 'react';
import './Layout.css';
import { Layout as AntLayout, ConfigProvider, App } from 'antd';
import RightSide from '../RightSide/RightSide';
import LeftSide from '../LeftSide/LeftSide';
import Navbar from './Navbar';
import EventManager from '../common/EventManager';
import { useStore } from '../../store/store';
import { getPrimaryDark, containerBackground, pageBackground } from '../../constants/constants';
import { hexToRgba } from '../../utils/theme';
import { pillButtonComponentsTheme } from '../../utils/styles';
import Middle from '../Middle/Middle';
import { OverlayProvider } from '../../contexts/OverlayContext';
import PreviewOverlay from '../common/PreviewOverlay';
import MultiContainerActionsToolbar from '../Container/MultiContainerActionsToolbar';

const { Content } = AntLayout;
const selector = ({
  proposalList,
  currentDocument,
  handleProposalList,
  handleMaskList,
  handleUnSelectBox,
  handleUnSelectContainer,
  currentProposal,
}) => ({
  proposalList,
  currentDocument,
  currentProposal,
  handleProposalList,
  handleMaskList,
  handleUnSelectBox,
  handleUnSelectContainer,
});

const Layout = () => {
  const {
    proposalList,
    currentDocument,
    currentProposal,
    handleProposalList,
    handleMaskList,
    handleUnSelectBox,
    handleUnSelectContainer,
  } = useStore(selector);

  useEffect(() => {
    if (currentDocument.type === 'template' || currentProposal.id) {
      handleProposalList(false);
      handleMaskList(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const accentColor = getPrimaryDark(currentDocument.type === 'template');

  // Couleur d'accent (rail actif, boutons, cases cochées, onglets...) alignée
  // sur celle du logo, qui change selon le mode (violet template / navy
  // proposal, cf. getPrimaryDark). Le token antd (ConfigProvider) couvre les
  // composants React ; les variables CSS couvrent les .less qui ne peuvent
  // pas lire le store (LeftSide.less, VariableHandler.less, Navbar.less...).
  useEffect(() => {
    document.documentElement.style.setProperty('--primary-color', accentColor);
    document.documentElement.style.setProperty('--primary-color-light', hexToRgba(accentColor, 0.3));
  }, [accentColor]);

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: accentColor,
          // Le chrome de l'éditeur a escaladé ses z-index bien au-delà du défaut
          // antd (1000) pour rester au-dessus du canvas : navbar 1100, barre
          // flottante des containers 9998, AdderMenu 10001, TableConfigDrawer
          // 10002. Résultat, les overlays antd passaient dessous — une modale de
          // confirmation était masquée par la barre flottante qui l'avait
          // déclenchée. On remonte toute l'échelle antd d'un cran au-dessus de ce
          // chrome plutôt que de baisser la barre, qui repasserait sous la navbar.
          zIndexPopupBase: 10100,
        },
        components: {
          ...pillButtonComponentsTheme,
          Select: {
            // antd dérive le fond de l'option sélectionnée de `colorPrimary`.
            // Notre accent est un navy désaturé, dont la dérivée sort en gris
            // soutenu : l'option sélectionnée ressemblait à une ligne désactivée
            // plutôt qu'à un choix courant. Un voile de l'accent la marque sans
            // l'assombrir, et la graisse (défaut antd) fait le reste.
            optionSelectedBg: hexToRgba(accentColor, 0.1),
          },
          // Même raison pour le Cascader (sélecteur de tableau, de variable).
          Cascader: {
            optionSelectedBg: hexToRgba(accentColor, 0.1),
          },
        },
      }}
    >
      <App>
      <OverlayProvider>
        <EventManager>
          <AntLayout
            data-role={pageBackground}
            onClick={(event) => {
              // Ne pas désélectionner la box lorsqu'on clique dans un Select Ant Design
              // (champ ou dropdown : ex. `shapeTypeSelector` dans la navbar)
              if (event.target && typeof event.target.closest === 'function') {
                if (
                  event.target.closest('.ant-select') ||
                  event.target.closest('.ant-select-dropdown')
                ) {
                  return;
                }
              }
              var selection = window.getSelection();
              if (!(event.ctrlKey || event.metaKey || event.shiftKey) && event.target.dataset.role === pageBackground &&
                selection.type !== "Range" && selection.type !== "None") {
                handleUnSelectBox();
                if (event.target.dataset.parent !== containerBackground) {
                  handleUnSelectContainer()
                }
              }
            }}>
            <Navbar />
            <Content style={{ zIndex: 1 }}>
              <Middle />
            </Content>
            {!proposalList && <LeftSide />}
            {!proposalList && <RightSide />}
            <div id="SelectionVariablePortal" />
            <div id="MenuBarPortal" />
            <PreviewOverlay />
            <MultiContainerActionsToolbar />
          </AntLayout>
        </EventManager>
      </OverlayProvider>
      </App>
    </ConfigProvider>
  );
};

export default Layout;
